import React, { useEffect, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FormRow } from '@gravity-ui/components';
import { Person } from '@gravity-ui/icons';
import { Button, Link, Loader, TextInput } from '@gravity-ui/uikit';
import { zodResolver } from '@hookform/resolvers/zod';

import { useSafeParams } from 'hooks';
import { useLazyUserCheckTokenQuery, useUserConfirmPassMutation } from 'store/api';

import { EyeButton } from '../../ui-kit';
import { InfoMessage } from '../../utils';
import {
	ADMINISTRATOR,
	CONFIRMATION_PASSWORD,
	EMAIL,
	PASSWORD,
	PASSWORD_MESSAGE,
	REGISTRATION,
	REGISTRATION_BTN,
	REGISTRATION_PROBLEM,
	YOUR_LOGIN,
} from '../constants';
import type { ConfirmPass } from '../utils';
import { InvalidLink } from '../utils';
import { useValidationConfirmPass } from '../utils/use-validation/use-validation-reg';

import styles from './styles.module.scss';

export const RegistrationConfirm = () => {
	const [hidePass, setHidePass] = useState<boolean>(true);
	const [showMessage, setShowMessage] = useState(false);
	const [tokenError, setTokenError] = useState(false);
	const { token } = useSafeParams();

	const [checkToken, { data: tokenResponse, isLoading: tokenLoading, isError: tokenCheckError }] =
		useLazyUserCheckTokenQuery();

	// Получаем email из ответа API
	const userEmail = useMemo(() => {
		return tokenResponse?.email || null;
	}, [tokenResponse]);

	// Проверяем токен при загрузке компонента для получения email
	useEffect(() => {
		if (token && !tokenResponse && !tokenLoading) {
			checkToken({ token }).catch(() => {
				// Если токен невалиден, это обработается в onSubmitHandler
			});
		}
	}, [token, tokenResponse, tokenLoading, checkToken]);

	const inputType = hidePass ? 'password' : undefined;

	const { resetPassSchema: schema } = useValidationConfirmPass();

	const methods = useForm<ConfirmPass>({
		resolver: zodResolver(schema),
		defaultValues: { password: '', confirm: '', token: token },
	});

	const [userConfirmPass, { isLoading: apiIsLoading, isError, error, isSuccess }] = useUserConfirmPassMutation();

	const {
		watch,
		register,
		handleSubmit,
		formState: { errors },
	} = methods;

	const formValues = watch();

	useEffect(() => {
		if (isSuccess) {
			toast.success('Успешно!');
			setShowMessage(true);
		}
		if (isError) {
			toast.error((error as any).data.message, {
				position: 'top-right',
			});
		}
		if (tokenCheckError) {
			setTokenError(true);
		}
	}, [isError, error, isSuccess, tokenCheckError]);

	const onSubmitHandler: SubmitHandler<ConfirmPass> = async (values) => {
		if (!token) {
			setTokenError(true);
			return;
		}

		// Если токен валиден (проверен при загрузке), отправляем форму
		userConfirmPass(values);
	};

	return (
		<>
			{(tokenLoading || apiIsLoading) && <Loader className={styles.loader} />}

			{!showMessage && !tokenError && token && (
				<div className={styles.reg}>
					<>
						<h1>{REGISTRATION}</h1>

						<div className={styles.reg__user}>
							<div className={styles.reg__user__icon}>
								<Person />
							</div>
							<div className={styles.reg__user__info}>
								<div className={styles.reg__user__info__name}>
									{YOUR_LOGIN}:{userEmail}
								</div>
							</div>
						</div>

						<FormProvider {...methods}>
							<form className={styles.reg__form}>
								<div className={styles.reg__form__group}>
									<FormRow label={PASSWORD} direction="column">
										<TextInput
											size="xl"
											{...register('password')}
											value={formValues.password}
											error={errors.password?.message || isError}
											note={<span>{PASSWORD_MESSAGE}</span>}
											type={inputType}
											endContent={
												<EyeButton opened={hidePass} onClick={() => setHidePass(!hidePass)} />
											}
										/>
									</FormRow>
								</div>

								<div className={styles.reg__form__group}>
									<FormRow label={CONFIRMATION_PASSWORD} direction="column">
										<TextInput
											size="xl"
											{...register('confirm')}
											value={formValues.confirm}
											error={errors.confirm?.message || isError}
											type={inputType}
											endContent={
												<EyeButton opened={hidePass} onClick={() => setHidePass(!hidePass)} />
											}
										/>
									</FormRow>
								</div>

								<Button
									onClick={handleSubmit(onSubmitHandler)}
									loading={tokenLoading || apiIsLoading}
									type="submit"
									size="xl"
									className={styles.reg__form__btn}
								>
									{REGISTRATION_BTN}
								</Button>
							</form>
						</FormProvider>

						<div className={styles.reg__help}>
							<span>{REGISTRATION_PROBLEM}</span>
							<span>
								{ADMINISTRATOR}{' '}
								<Link view="normal" href="mailto:edu@fond-igra.ru">
									{EMAIL}
								</Link>
							</span>
						</div>
					</>
				</div>
			)}

			{showMessage && <InfoMessage value="" userRegistration />}
			{(tokenError || !token) && <InvalidLink />}
		</>
	);
};
